/**
 * Pack quantity rules: enforces min/step from product tags (Pack-of-5, pack-of-10, etc.)
 * and max quantity from limit_max_qty metafield or variant.quantity_rule.max or available stock.
 * Shows sold-out when inventory < pack min.
 * Runs on quantity-input elements or inputs with data-pack-min, data-pack-step, data-pack-max, data-pack-stock-max, data-pack-sold-out.
 */
(function () {
  const SOLD_OUT_TEXT = 'Sold out';
  const MAX_REACHED_TEXT = 'Max quantity reached';

  function clampToStep(value, min, step) {
    if (value < min) return min;
    const remainder = (value - min) % step;
    if (remainder === 0) return value;
    return value - remainder + step;
  }

  function clampToMax(value, max) {
    if (max === null || max === undefined || isNaN(max)) return value;
    return Math.min(value, max);
  }

  function findParentWrapper(element) {
    let current = element;
    while (current) {
      if (current.tagName === 'QUANTITY-INPUT') {
        return current;
      }
      if (current.classList && (
        current.classList.contains('cart-item__quantity-controls') ||
        current.classList.contains('cart-item__quantity-wrapper') ||
        current.classList.contains('cart-item__quantity-section')
      )) {
        return current;
      }
      current = current.parentElement;
    }
    return element.closest('quantity-input') || element.closest('.cart-item__quantity-controls') || element;
  }

  function getEffectiveMax(wrapper, packMax, packStockMax) {
    if (packMax !== null && packMax !== undefined && packStockMax !== null && packStockMax !== undefined) {
      return Math.min(packMax, packStockMax);
    }
    if (packMax !== null && packMax !== undefined) {
      return packMax;
    }
    if (packStockMax !== null && packStockMax !== undefined) {
      return packStockMax;
    }
    return null;
  }

  function applyPackRulesToInput(input) {
    const wrapper = findParentWrapper(input);
    const packMin = parseInt(input.dataset.packMin || wrapper.dataset.packMin, 10);
    const packStep = parseInt(input.dataset.packStep || wrapper.dataset.packStep, 10);
    const packMax = (input.dataset.packMax || wrapper.dataset.packMax) ? parseInt(input.dataset.packMax || wrapper.dataset.packMax, 10) : null;
    const packStockMax = (input.dataset.packStockMax || wrapper.dataset.packStockMax) ? parseInt(input.dataset.packStockMax || wrapper.dataset.packStockMax, 10) : null;
    const soldOut = (input.dataset.packSoldOut || wrapper.dataset.packSoldOut) === 'true';

    const effectiveMax = getEffectiveMax(wrapper, packMax, packStockMax);

    if (isNaN(packMin) || isNaN(packStep)) return null;

    const btnMinus = wrapper.querySelector('.quantity__button[name="minus"], .cart-item__quantity-btn--decrease');
    const btnPlus = wrapper.querySelector('.quantity__button[name="plus"], .cart-item__quantity-btn--increase');

    if (soldOut) {
      input.disabled = true;
      if (btnMinus) btnMinus.disabled = true;
      if (btnPlus) btnPlus.disabled = true;
      wrapper.classList.add('pack-quantity-sold-out');
      wrapper.classList.remove('pack-quantity-max-reached');
      if (!wrapper.querySelector('.pack-quantity-sold-out-message')) {
        const msg = document.createElement('span');
        msg.className = 'pack-quantity-sold-out-message';
        msg.setAttribute('aria-live', 'polite');
        msg.textContent = SOLD_OUT_TEXT;
        wrapper.appendChild(msg);
      }
      const existingMaxMsg = wrapper.querySelector('.pack-quantity-max-message');
      if (existingMaxMsg) existingMaxMsg.remove();
      const productForm = wrapper.closest('form[action*="/cart/add"], form[action*="/cart"]');
      if (productForm) {
        const submitBtn = productForm.querySelector('[name="add"], [name="checkout"], [type="submit"]');
        if (submitBtn) submitBtn.disabled = true;
      }
      return wrapper;
    }

    wrapper.classList.remove('pack-quantity-sold-out');
    const existingMsg = wrapper.querySelector('.pack-quantity-sold-out-message');
    if (existingMsg) existingMsg.remove();

    input.step = packStep;
    if (effectiveMax !== null) {
      input.max = effectiveMax;
    }

    input.dataset.min = String(packMin);

    let val = parseInt(input.value, 10) || 0;
    if (val > 0) {
      val = clampToStep(val, packMin, packStep);
      if (val < packMin) val = packMin;
      if (effectiveMax !== null) {
        val = clampToMax(val, effectiveMax);
      }
      input.value = val;
    }

    const currentVal = parseInt(input.value, 10);
    if (btnMinus) btnMinus.disabled = false;
    if (btnPlus) {
      if (effectiveMax !== null && currentVal >= effectiveMax) {
        btnPlus.disabled = true;
        wrapper.classList.add('pack-quantity-max-reached');
        if (!wrapper.querySelector('.pack-quantity-max-message')) {
          const maxMsg = document.createElement('span');
          maxMsg.className = 'pack-quantity-max-message';
          maxMsg.setAttribute('aria-live', 'polite');
          maxMsg.textContent = MAX_REACHED_TEXT;
          wrapper.appendChild(maxMsg);
        }
      } else {
        btnPlus.disabled = false;
        wrapper.classList.remove('pack-quantity-max-reached');
        const existingMaxMsg = wrapper.querySelector('.pack-quantity-max-message');
        if (existingMaxMsg) existingMaxMsg.remove();
      }
    }
    input.disabled = false;

    const productForm = wrapper.closest('form[action*="/cart/add"], form[action*="/cart"]');
    if (productForm) {
      const submitBtn = productForm.querySelector('[name="add"], [name="checkout"], [type="submit"]');
      if (submitBtn) submitBtn.disabled = false;
    }

    return wrapper;
  }

  function validateAndClampInput(input) {
    const wrapper = findParentWrapper(input);
    const packMin = parseInt(input.dataset.packMin || wrapper.dataset.packMin, 10);
    const packStep = parseInt(input.dataset.packStep || wrapper.dataset.packStep, 10);
    const packMax = (input.dataset.packMax || wrapper.dataset.packMax) ? parseInt(input.dataset.packMax || wrapper.dataset.packMax, 10) : null;
    const packStockMax = (input.dataset.packStockMax || wrapper.dataset.packStockMax) ? parseInt(input.dataset.packStockMax || wrapper.dataset.packStockMax, 10) : null;
    const soldOut = (input.dataset.packSoldOut || wrapper.dataset.packSoldOut) === 'true';

    const effectiveMax = getEffectiveMax(wrapper, packMax, packStockMax);

    if (soldOut || isNaN(packMin) || isNaN(packStep)) return;

    const btnPlus = wrapper.querySelector('.quantity__button[name="plus"], .cart-item__quantity-btn--increase');

    let val = parseInt(input.value, 10);
    let isInvalid = false;
    
    if (isNaN(val) || val === 0) {
      val = packMin;
      isInvalid = true;
    } else if (val < packMin) {
      val = packMin;
      isInvalid = true;
    } else { 
      const remainder = (val - packMin) % packStep;
      if (remainder !== 0) {
        val = clampToStep(val, packMin, packStep);
        isInvalid = true;
      }
      if (effectiveMax !== null && val > effectiveMax) {
        val = clampToMax(val, effectiveMax);
      } 
    }
    input.value = val;

    const currentVal = parseInt(input.value, 10);
    
    if (isInvalid && !soldOut) {
      wrapper.classList.add('pack-quantity-invalid');
      if (!wrapper.querySelector('.pack-quantity-invalid-message')) {
        const invalidMsg = document.createElement('span');
        invalidMsg.className = 'pack-quantity-invalid-message';
        invalidMsg.setAttribute('aria-live', 'polite');
        invalidMsg.textContent = 'Min: ' + packMin;
        wrapper.appendChild(invalidMsg);
      }
    } else {
      wrapper.classList.remove('pack-quantity-invalid');
      const existingInvalidMsg = wrapper.querySelector('.pack-quantity-invalid-message');
      if (existingInvalidMsg) existingInvalidMsg.remove();
    }

    if (btnPlus) {
      if (effectiveMax !== null && currentVal >= effectiveMax) {
        btnPlus.disabled = true;
        wrapper.classList.add('pack-quantity-max-reached');
        if (!wrapper.querySelector('.pack-quantity-max-message')) {
          const maxMsg = document.createElement('span');
          maxMsg.className = 'pack-quantity-max-message';
          maxMsg.setAttribute('aria-live', 'polite');
          maxMsg.textContent = MAX_REACHED_TEXT;
          wrapper.appendChild(maxMsg);
        }
      } else {
        btnPlus.disabled = false;
        wrapper.classList.remove('pack-quantity-max-reached');
        const existingMaxMsg = wrapper.querySelector('.pack-quantity-max-message');
        if (existingMaxMsg) existingMaxMsg.remove();
      }
    }
  }

  function init() {
    document.querySelectorAll('quantity-input[data-pack-min]').forEach((wrapper) => {
      if (wrapper.dataset.packRulesApplied) return;
      wrapper.dataset.packRulesApplied = '1';
      const input = wrapper.querySelector('.quantity__input');
      if (input) {
        applyPackRulesToInput(input);
        input.addEventListener('change', () => validateAndClampInput(input));
        input.addEventListener('input', () => validateAndClampInput(input));
        input.addEventListener('blur', () => validateAndClampInput(input));
      }
      wrapper.querySelectorAll('.quantity__button').forEach((btn) => {
        btn.addEventListener('click', () => {
          setTimeout(() => {
            const inp = wrapper.querySelector('.quantity__input');
            if (inp) validateAndClampInput(inp);
          }, 0);
        });
      });
    });

    document.querySelectorAll('input[data-pack-min], input.quantity__input[data-min], input.cart-item__quantity-input[data-min]').forEach((input) => {
      if (input.dataset.packRulesApplied) return;
      const wrapper = findParentWrapper(input);
      if (wrapper.dataset.packRulesApplied) return;
      
      input.dataset.packRulesApplied = '1';
      applyPackRulesToInput(input);
      
      input.addEventListener('change', () => validateAndClampInput(input));
      input.addEventListener('input', () => validateAndClampInput(input));
      input.addEventListener('blur', () => validateAndClampInput(input));
      
      const btnMinus = wrapper.querySelector('.quantity__button[name="minus"], .cart-item__quantity-btn--decrease');
      const btnPlus = wrapper.querySelector('.quantity__button[name="plus"], .cart-item__quantity-btn--increase');
      
      if (btnMinus) {
        btnMinus.addEventListener('click', () => {
          setTimeout(() => validateAndClampInput(input), 0);
        });
      }
      if (btnPlus) {
        btnPlus.addEventListener('click', () => {
          setTimeout(() => validateAndClampInput(input), 0);
        });
      }

      const form = wrapper.closest('form[action*="/cart/add"]');
      if (form && !form.dataset.packValidationAdded) {
        form.dataset.packValidationAdded = '1';
        form.addEventListener('submit', (e) => {
          const qtyInput = form.querySelector('.quantity__input, quantity-input .quantity__input, input[name="quantity"]');
          if (qtyInput && qtyInput.dataset.packMin) {
            const val = parseInt(qtyInput.value, 10) || 0;
            if (val === 0 || val < parseInt(qtyInput.dataset.packMin, 10)) {
              e.preventDefault();
              qtyInput.value = qtyInput.dataset.packMin;
              qtyInput.focus();
              return false;
            }
            const step = parseInt(qtyInput.dataset.packStep || qtyInput.step, 10) || 1;
            const min = parseInt(qtyInput.dataset.packMin, 10);
            const remainder = (val - min) % step;
            if (remainder !== 0) {
              e.preventDefault();
              qtyInput.value = val - remainder + step;
              qtyInput.focus();
              return false;
            }
          }
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  const observer = new MutationObserver((mutations) => {
    let shouldInit = false;
    mutations.forEach((m) => {
      m.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          if (node.querySelector?.('quantity-input[data-pack-min]') || node.matches?.('quantity-input[data-pack-min]')) {
            shouldInit = true;
          }
          if (node.querySelector?.('input[data-pack-min]') || node.matches?.('input[data-pack-min]')) {
            shouldInit = true;
          }
        }
      });
    });
    if (shouldInit) {
      document.querySelectorAll('quantity-input[data-pack-min], input[data-pack-min]').forEach((el) => {
        if (el.dataset.packRulesApplied) {
          delete el.dataset.packRulesApplied;
        }
      });
      init();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
